import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@/lib/zodcompat'
import { toast } from 'sonner'
import { ArrowLeft } from 'lucide-react'
import { companySchema, type CompanyFormData } from '@/validators/company'
import { useCompany, useCreateCompany, useUpdateCompany } from '@/hooks/useCompanies'
import { FormSection } from '@/components/form/form-section'
import { CnpjInput } from '@/components/form/cnpj-input'
import { PhoneInput } from '@/components/form/phone-input'
import { AddressFields } from '@/components/form/address-fields'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Button } from '@/components/ui/button'
import { LoadingButton } from '@/components/ui/loading-button'

export function CompanyFormPage() {
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const isEdit = !!id

  const { data: company, isLoading: loadingCompany } = useCompany(id ?? '')
  const createMutation = useCreateCompany()
  const updateMutation = useUpdateCompany(id ?? '')
  const mutation = isEdit ? updateMutation : createMutation

  const {
    register,
    handleSubmit,
    control,
    setValue,
    reset,
    formState: { errors },
  } = useForm<CompanyFormData>({
    resolver: zodResolver(companySchema),
    defaultValues: {
      tradeName: '',
      legalName: '',
      cnpj: '',
      stateRegistration: '',
      phone: '',
      email: '',
      contactPerson: '',
      zipCode: '',
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
    },
  })

  useEffect(() => {
    if (company) {
      reset({
        tradeName: company.tradeName,
        legalName: company.legalName,
        cnpj: company.cnpj,
        stateRegistration: company.stateRegistration ?? '',
        phone: company.phone ?? '',
        email: company.email ?? '',
        contactPerson: company.contactPerson ?? '',
        zipCode: company.zipCode ?? '',
        street: company.street ?? '',
        number: company.number ?? '',
        complement: company.complement ?? '',
        neighborhood: company.neighborhood ?? '',
        city: company.city ?? '',
        state: company.state ?? '',
      })
    }
  }, [company, reset])

  async function onSubmit(data: CompanyFormData) {
    try {
      await mutation.mutateAsync(data)
      toast.success(isEdit ? 'Empresa atualizada!' : 'Empresa cadastrada!')
      navigate('/companies')
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { error?: { message?: string } } } })
        ?.response?.data?.error?.message
      toast.error(msg ?? 'Erro ao salvar empresa')
    }
  }

  if (isEdit && loadingCompany) {
    return (
      <div className="flex h-32 items-center justify-center">
        <p className="text-sm text-muted-foreground">Carregando…</p>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-2xl space-y-6">
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="icon-sm" onClick={() => navigate('/companies')}>
          <ArrowLeft size={16} />
        </Button>
        <div>
          <h1 className="text-xl font-semibold text-foreground">
            {isEdit ? 'Editar empresa' : 'Nova empresa'}
          </h1>
          <p className="text-sm text-muted-foreground">
            {isEdit ? company?.tradeName : 'Preencha os dados da empresa'}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        <FormSection title="Dados da empresa">
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="tradeName">Nome fantasia *</Label>
              <Input
                id="tradeName"
                aria-invalid={!!errors.tradeName}
                {...register('tradeName')}
              />
              {errors.tradeName && (
                <p className="text-xs text-destructive">{errors.tradeName.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="stateRegistration">Inscrição estadual</Label>
              <Input id="stateRegistration" {...register('stateRegistration')} />
            </div>
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="legalName">Razão social *</Label>
            <Input
              id="legalName"
              aria-invalid={!!errors.legalName}
              {...register('legalName')}
            />
            {errors.legalName && (
              <p className="text-xs text-destructive">{errors.legalName.message}</p>
            )}
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <CnpjInput control={control} name="cnpj" />
            <PhoneInput control={control} name="phone" />
          </div>

          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
            <div className="space-y-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                aria-invalid={!!errors.email}
                {...register('email')}
              />
              {errors.email && (
                <p className="text-xs text-destructive">{errors.email.message}</p>
              )}
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="contactPerson">Contato</Label>
              <Input id="contactPerson" {...register('contactPerson')} />
            </div>
          </div>
        </FormSection>

        <AddressFields
          register={register}
          errors={errors}
          setValue={setValue}
          control={control}
          zipCodeName="zipCode"
          streetName="street"
          numberName="number"
          complementName="complement"
          neighborhoodName="neighborhood"
          cityName="city"
          stateName="state"
        />

        <div className="flex justify-end gap-2">
          <Button
            type="button"
            variant="outline"
            onClick={() => navigate('/companies')}
            disabled={mutation.isPending}
          >
            Cancelar
          </Button>
          <LoadingButton
            type="submit"
            loading={mutation.isPending}
            loadingText="Salvando…"
          >
            {isEdit ? 'Salvar alterações' : 'Cadastrar empresa'}
          </LoadingButton>
        </div>
      </form>
    </div>
  )
}
